import PlayerTypeConfig from '../config/player-type-config';

/**
 *
 * @module AbstractPlayer
 * @desc Abstract implementation of the MediaPlayer. All concrete player will implement the AbstractPlayer.
 * @class AbstractPlayer
 *
 */
class AbstractPlayer {

    /**
     *
     * @constructor
     * @param {MediaPlayerConfig} mediaPlayerConfig
     *
     */
    constructor(mediaPlayerConfig, observationCenter) {

        /**
         *
         * @type {MediaPlayerConfig}
         * @desc The MediaPlayerConfig object for the player settings and configuration.
         *
         */
        this.mediaPlayerConfig = mediaPlayerConfig;

        /**
         *
         * @type {PlayerTypeConfig}
         * @desc The PlayerTypeConfig object for the player
         *
         */
        this.playerTypeConfig = mediaPlayerConfig.playerTypeConfig;

        /**
         *
         * @type {DOMElement}
         * @desc Reference to the parent element the player will be attached to. Created when the player is
         * initialized and based on the `domTarget` id property passed in via the MediaPlayerConfig.
         *
         */
        this.parentEl = undefined;

        /**
         *
         * @type {DOMElement}
         * @desc The video element created for playback by the concrete implementation.
         *
         */
        this.videoEl = undefined;

        /**
        * @type {ObservationCenter}
        * @desc The Observation Center created by the media player
        */
        this.observationCenter = observationCenter;

    }

    /**
     *
     * @desc Initializes the player and creates the DOM Element
     * @abstract
     *
     */
    initPlayer() {

        throw new Error('Implement initPlayer()');

    }

    /**
     *
     * @desc Sets the `parentEl` property with a reference to the player node's parent DOMElement.
     * @abstract
     *
     */
    findParentElement() {

        throw new Error('Implement findParentElement()');

    }

    /**
     *
     * @desc Creates the player's DOMElement
     * @abstract
     *
     */
    createPlayerElement() {

        throw new Error('Implement createPlayerElement()');

    }

    /**
     *
     * @desc Attaches the player `DOMElement` to the `parentEl`
     * @abstract
     *
     */
    attachPlayerElement() {

        throw new Error('Implement attachPlayerElement()');

    }

    /**
     *
     * @desc Sets the source of the video for playback
     * @param {MediaItem} mediaItem The MediaItem representing media to play.
     * @abstract
     *
     */
    open(mediaItem) {

        throw new Error(`Implement open(media) ${mediaItem}`);

    }

    /**
     *
     * @desc Replays the current clip
     * @abstract
     *
     */
    replay() {

        throw new Error('Implement replay()');

    }

    /**
     *
     * @param {string} src The playback URL
     * @abstract
     *
     */
    canPlay(src) {

        throw new Error('Implement canPlay(src)');

    }

    /**
     *
     * @desc Set poster image for the player
     * @param {string} imgUrl
     * @memberof AbstractPlayer
     * @abstract
     *
     */
    setPoster(imgUrl) {

        throw new Error(`Implement setPoster(imgUrl) ${imgUrl}`);

    }

    /**
     *
     * @desc resume playing when paused or stopped
     * @abstract
     *
     */
    play() {

        throw new Error(`Implement play()`);

    }

    /**
     *
     * @desc Pause playback
     * @abstract
     *
     */
    pause() {

        throw new Error(`Implement pause()`);

    }

    /**
     *
     * @desc Pause playback
     * @abstract
     *
     */
    seek() {

        throw new Error(`Implement seek()`);

    }

    /**
     *
     * @desc Mute the player
     * @abstract
     *
     */
    mute() {

        throw new Error(`Implement mute()`);

    }

    /**
     *
     * @desc Un-mute the player
     * @abstract
     *
     */
    unMute() {

        throw new Error(`Implement unMute()`);

    }

    /**
     *
     * @desc Sets isAdPlaying on the state and updates any observers
     * @param {boolean} value if an ad is playing or not
     * @abstract
     *
     */
    setIsAdPlaying(value) {

        throw new Error(`Implement setIsAdPlaying(value)`);

    }

    /**
     *
     * @desc Abstract. See concrete implementation for details.
     * @param {String} message The message to log.
     * @abstract
     *
     */
    log(message) {

        throw new Error(`Implement log(message) ${message}`);

    }

    /**
     *
     * @desc Abstract. See concrete implementation for details.
     * @abstract
     *
     */
    toString() {

        throw new Error(`Implement toString()`);

    }

}

export default AbstractPlayer;
